import { useState, useEffect, useCallback, useRef } from 'react';

interface SliderOptions {
  autoplay?: boolean;
  interval?: number;
}

export const useSlider = <T>(items: T[], options: SliderOptions = {}) => {
  const { autoplay = true, interval = 7000 } = options;
  const [currentIndex, setCurrentIndex] = useState(0);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const next = useCallback(() => {
    setCurrentIndex((prev) => (prev >= items.length - 1 ? 0 : prev + 1));
  }, [items.length]);

  const prev = useCallback(() => {
    setCurrentIndex((prev) => (prev <= 0 ? items.length - 1 : prev - 1));
  }, [items.length]);

  const goTo = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const startAutoplay = useCallback(() => {
    if (autoplay) {
      autoplayRef.current = setInterval(next, interval);
    }
  }, [autoplay, interval, next]);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
  }, []);

  useEffect(() => {
    startAutoplay();
    return () => stopAutoplay();
  }, [startAutoplay, stopAutoplay]);

  return {
    currentIndex,
    next,
    prev,
    goTo,
    stopAutoplay,
    startAutoplay,
  };
};
